import { useEffect, useRef, useState } from "react";
import {
  Frame,
  Glass,
  GlassContainer,
  Html,
  LiquidCanvas,
  ZStack,
} from "@liquid-dom/react";
import { DEFAULT_LIGHT_DIRECTION, useLiquidGlassLight, useLiquidGlassSupport } from "../hooks/useLiquidGlass";

// Global liquid-glass wrapper: pass any element via `as` and any children,
// and it grows a WebGPU glass backdrop behind them. This is the one
// primitive to reach for when making a new piece of UI "liquid glass" —
// LiquidGlassButton builds on the same hooks for control-shaped content.
export default function LiquidGlass({
  as: Component = "div",
  children,
  className = "",
  tone = "dark",
  cornerRadius = 26,
  bezelWidth = 28,
  thickness = 96,
  blur = 14,
  shadowOffsetY = 10,
  shadowBlur = 28,
  tint,
  specularOpacity = 0.7,
  specularStrength = 1.05,
  style,
  ...props
}) {
  const surfaceRef = useRef(null);
  const htmlInCanvasEnabled = useLiquidGlassSupport();
  const { glassContainerRef, onPointerMove, onPointerLeave } = useLiquidGlassLight();
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!htmlInCanvasEnabled || !surfaceRef.current) return undefined;

    const updateSize = () => {
      const rect = surfaceRef.current?.getBoundingClientRect();
      if (!rect) return;
      setSize({
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      });
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(surfaceRef.current);
    return () => observer.disconnect();
  }, [htmlInCanvasEnabled]);

  const handlePointerMove = (event) => {
    props.onPointerMove?.(event);
    onPointerMove(event);
  };

  const handlePointerLeave = (event) => {
    props.onPointerLeave?.(event);
    onPointerLeave(event);
  };

  return (
    <Component
      {...props}
      ref={surfaceRef}
      className={`${className}${htmlInCanvasEnabled ? ` liquid-glass-surface-enabled liquid-glass-surface-${tone}` : ""}`}
      style={style}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {htmlInCanvasEnabled && size.width > 0 && size.height > 0 ? (
        <LiquidCanvas
          className="liquid-glass-surface-canvas"
          canvasClassName="liquid-glass-surface-canvas-element"
          proposal={size}
          maxDpr={2}
          frameloop="demand"
        >
          <ZStack alignment="center">
            <Frame width={size.width} height={size.height}>
              <Html zIndex={-2} sizing="fill">
                <div className={`liquid-glass-surface-backdrop liquid-glass-surface-backdrop-${tone}`} />
              </Html>
            </Frame>
            <GlassContainer
              ref={glassContainerRef}
              lightDirection={DEFAULT_LIGHT_DIRECTION}
              blur={blur}
              bezelWidth={bezelWidth}
              thickness={thickness}
              tint={tint ?? (tone === "light"
                ? { r: 1, g: 1, b: 1, a: 0.2 }
                : { r: 0.72, g: 0.8, b: 1, a: 0.12 })}
              shadowColor={{ r: 0, g: 0, b: 0, a: tone === "light" ? 0.15 : 0.3 }}
              shadowOffsetY={shadowOffsetY}
              shadowBlur={shadowBlur}
              specularOpacity={specularOpacity}
              specularWidth={1}
              specularStrength={specularStrength}
              specularSharpness={3}
              oppositeSpecularStrength={0.45}
            >
              <Frame width={size.width} height={size.height}>
                <Glass cornerRadius={cornerRadius} cornerSmoothing={0.7} />
              </Frame>
            </GlassContainer>
          </ZStack>
        </LiquidCanvas>
      ) : null}
      {children}
    </Component>
  );
}
